import { TrackJS } from 'trackjs';

import { uploadFile } from '../../transfer-bundle/services/multipart-uploader/upload-file';
import { ChunksQueue } from '../../transfer-bundle/services/queues/chunks-queue';
import { TransferAPI } from '../../transfer-bundle/services/api/transfer';
import { processChunkTask } from '../../transfer-bundle/services/multipart-uploader/upload-chunk';

import TransferItem from './transfer-item';

import API from './api';

import TransferConfig, { FREE_EXPIRY, FREE_EXPIRY_ON_CHANNEL } from './config';

import Logger from '@module/logger';
import TransferError from '@app/helper/error';
import TransferErrorMessages from '@app/helper/error/messages';
import * as TransferItemsHelper from '@app/helper/transfer-items';
import {
  isTransferSizeUnlimited,
  hasStarted,
} from '@app/transfer-bundle/helpers';

import Build, {
  normalizeTransfer,
  convertExpiry,
} from '@module/transfer/build';

/*
 * plus == never
 * free && onChannel == 4 weeks
 * free && not on channel == 1 week
 */
const determineExpiry = (userType, onChannel) => {
  if (userType === 'plus') {
    return 'never';
  }

  if (onChannel) {
    return FREE_EXPIRY_ON_CHANNEL;
  }

  return FREE_EXPIRY;
};

let chunksQueue = null;

export default class Transfer {
  static pauseUpload() {
    if (chunksQueue) {
      chunksQueue.pause();
    }
  }

  static resumeUpload() {
    if (chunksQueue) {
      chunksQueue.resume();
    }
  }

  // Static method that loads transfer details based on their public id and secret.
  static async load(transferId, secret, recipientId = false, password = false) {
    if (!transferId || !secret) {
      throw 'InvalidDownload';
    }
    return await API.fetchTransfer(transferId, secret, recipientId, password);
  }

  // Constructor for the Transfer object
  constructor({
    userType = 'free',
    type = 'email',
    onChannel = false,
    accountId,
  } = {}) {
    // This resets the counter for iOS filenames
    TransferItem.resetFilenameCounter();

    this.id = false; // the public id
    this.parent_id = false; // the parent id (for forwarding)
    this.state = 'init'; // init, started, paused, aborted, finished[, downloadable, downloading]
    this.shortURL = null; // the short (or long) url

    this.setUserType(userType);
    this.type = type === 'link' ? 'link' : 'email'; // The type decides whether it is a `link` or `email` transfer.

    this.language = 'en'; // What language is this?
    this.files = []; // Contains TransferItem objects
    this.items = [];
    this.itemsTree = TransferItemsHelper.buildItemsTree();
    this.sender = ''; // Contains e-mail address
    this.message = ''; // Contains transfer message
    this.channel = null; // Contains channel name
    this.password = null; // Contains a transfer password
    this.recipients = []; // Contains recipients (e-mail addresses)
    this.domain_user_id = null;
    this.uploadStartedAt = 0;

    if (accountId) {
      this.accountId = accountId;
    }

    // Determine expiry based on user type and if it is on the channel, or, not.
    this.expiry = determineExpiry(this.userType, onChannel);
  }

  static buildFromParams(json = {}, password = false) {
    const transfer = new Transfer();

    json = normalizeTransfer(json);
    if (json.items) {
      transfer.items = json.items;
    }
    transfer.itemsTree = json.itemsTree;
    transfer.title = json.title;

    if (json.id) {
      transfer.id = json.id;
    }
    if (json.shortened_url) {
      transfer.shortURL = json.shortened_url;
    }
    if (json.security_hash) {
      transfer.secret = json.security_hash;
    }
    if (json.recipient_id) {
      transfer.recipient_id = json.recipient_id;
    }
    if (json.state) {
      transfer.state = json.state;
    }
    if (json.message) {
      transfer.message = json.message;
    }
    if (json.account_id) {
      transfer.accountId = json.account_id;
    }

    if ('description' in json) {
      transfer.message = json.description;
    }
    if ('expiry_in_seconds' in json) {
      transfer.expiry_seconds = json.expiry_in_seconds;
    }
    if ('version' in json) {
      transfer.version = json.version;
    }
    if ('versioning' in json) {
      transfer.versioning = json.versioning;
    }

    if (transfer.isProcessing) {
      transfer.state = 'processing';
    }

    // If we are password protected and don't know the password, change the state
    if (
      (transfer.state === 'processing' || transfer.state === 'downloadable') &&
      json.password_protected === true &&
      !password
    ) {
      transfer.state = 'password_protected';
    }

    // If we are password protected and have the password, remember if for later.
    if (json.password_protected && password) {
      transfer.password = password;
    }

    if ('show_feedback' in json) {
      transfer.showFeedback = json.show_feedback;
    }

    return transfer;
  }

  setUserType(userType, expiry = false) {
    this.userType = userType;

    // We'll need to reset some things for free users.
    if (this.userType !== 'plus') {
      this.expiry = FREE_EXPIRY;
    } else if (expiry !== false) {
      this.expiry = expiry;
    }
  }

  add(files, allowedSize) {
    // Are we still on time to add files?
    if (hasStarted(this.state, this.id)) {
      throw 'TransferStarted';
    }

    // Make sure we have received 1 or more files
    if (!files) {
      throw 'MissingFiles';
    }

    // Make sure the files input is an Array/FileList/etc.
    // Array.isArray would return false for FileList
    if (typeof files.length === 'undefined') {
      files = [files];
    }

    let withDuplicates = false;
    const validatedFiles = Array.prototype.reduce.call(
      files,
      (result, file) => {
        const transferItem = new TransferItem(file);

        const topLevelItemName = TransferItemsHelper.getSubNodesFromItemName(
          // Relative path only exists if item was dropped into the window.
          // In the case when it was selected through the input control,
          // it will only have `name` property.
          file.relativePath || file.name
        ).shift();
        if (
          this.itemsTree.items.find((item) => item.name === topLevelItemName)
        ) {
          withDuplicates = true;
          return result;
        }

        return [...result, transferItem];
      },
      []
    );

    // Calculate bytes to be added
    if (!this.canFilesBeAdded(validatedFiles, allowedSize)) {
      throw 'TooManyBytes';
    }

    // Calculate amount of files allowed
    const totalFileCount = validatedFiles.length + this.files.length;
    if (totalFileCount > TransferConfig.maximumNumberOfFiles) {
      throw 'TooManyFiles';
    }

    // Add to the current list of file entries
    Array.prototype.push.apply(this.files, validatedFiles);

    // Only now throw error because we chose to allow non-duplicate filenames
    // to still be added to the list, instead of just blocking everything on error
    if (withDuplicates) {
      throw 'DuplicateFilename';
    }
  }

  remove(fileName) {
    this.files = this.files.filter((file) => file.name !== fileName);
  }

  removeDirectory(directoryName) {
    const path = `${directoryName}/`;
    this.files = this.files.filter(
      (file) => file.name !== directoryName && !file.name.startsWith(path)
    );
  }

  pause() {
    if (this.state !== 'started') {
      throw 'TransferNotStarted';
    }
    this.state = 'paused';

    Transfer.pauseUpload();
  }

  resume() {
    if (this.state !== 'paused') {
      throw 'TransferNotStarted';
    }
    this.state = 'started';

    Transfer.resumeUpload();
  }

  reset() {
    // Abort queue
    if (chunksQueue) {
      chunksQueue.abort(true);
    }
    chunksQueue = false;

    // Abort in API
    if (this.id) {
      API.cancelTransfer(this.id);
    }

    // Abort running API requests
    API.abort();

    // Reset transfer id
    this.id = false;

    // Cleanup files
    this.files.forEach((file) => file.reset());

    // Set state
    this.state = 'init';
  }

  cancel() {
    if (this.state !== 'started' && this.state !== 'paused') {
      throw 'TransferNotStarted';
    }
    this.reset();
  }

  abort(browserExit = false) {
    // If the browserExit flag is set to true, don't actually
    // abort or cancel the current transfer. Trust us, we're on the internet.
    if (browserExit) {
      if (this.id && (this.state === 'started' || this.state === 'paused')) {
        API.exitTransfer(this.id);
      }
      return;
    }

    // Abort queue
    // NOTE: This is also done in .cancel() however there it
    // is done silently. This will shout under the right circumstances.
    if (chunksQueue) {
      chunksQueue.abort();
    }
    chunksQueue = false;

    // Cancel the transfer
    this.cancel();

    // Set state
    this.state = 'aborted';
  }

  canFilesBeAdded(files, allowedSize) {
    // For unlimited size transfers we don't have to calculate sizes
    if (isTransferSizeUnlimited(allowedSize)) {
      return true;
    }

    const bytesToAdd = files.reduce((count, file) => file.size + count, 0);
    const transferSizeWithFiles = this.totalSize + bytesToAdd;
    return transferSizeWithFiles < allowedSize;
  }

  get isForward() {
    return !!this.parent_id;
  }

  get totalSize() {
    return this.items.reduce((totalSize, { size }) => {
      const fileSize = Number(size);
      if (!isNaN(fileSize)) {
        return totalSize + fileSize;
      }
      return totalSize;
    }, 0);
  }

  getAvailableSize(allowedSize) {
    return allowedSize - this.totalSize;
  }

  async validateRecaptcha(token) {
    try {
      if (!this.id) {
        throw new TransferError('RecaptchaFailed');
      }
      this.state = 'init';
      await API.validateRecaptcha(this.id, {
        token,
        expire_in: convertExpiry(this.expiry),
      });
    } catch (e) {
      this.state = 'recaptcha';
      throw e;
    }
    return this;
  }

  async start() {
    // Create a new Transfer
    try {
      const transferDetails = await API.fetchTransferId(this.type, Build(this));
      if (this.isForward) {
        if (!transferDetails || !transferDetails.id) {
          throw new TransferError('MissingTransferId');
        }
        this.expiry_seconds = transferDetails.expiry_in_seconds;
        this.shortURL = transferDetails.shortened_url;
      }

      this.id = transferDetails.id;

      if (!this.id) {
        throw new TransferError('InvalidTransferId');
      }

      this.verification_required =
        transferDetails.message === 'email_verification_required';
    } catch (e) {
      // FIXME: Temporary check if error is string because request method in transfer/api is not updated yet with custom errors
      if (e === 'RequestAborted') {
        throw new TransferError('TransferCanceled');
      }

      this.state = 'aborted';

      this.trackTransferError(e);

      const isErrorObject = typeof e === 'object';
      const isSpecificError =
        (isErrorObject &&
          [
            'LimitReached',
            'UserBlocked',
            'UnavailableParent',
            'HumanVerificationRequired',
          ].indexOf(e.message) !== -1) ||
        [TransferErrorMessages.TOO_MANY_RECIPIENTS].indexOf(e);

      if (isSpecificError) {
        throw e;
      }

      throw new TransferError('TransferFailed', { transferId: this.id });
    }

    // Return the details
    return this;
  }

  async upload() {
    let transferDetails;

    this.state = 'started';

    this.uploadStartedAt = Date.now();

    // If it is not a forward
    if (!this.isForward) {
      try {
        await this.uploadFiles();
      } catch (error) {
        Logger.error(error);
        this.trackTransferError(error);

        if (error === 'TransferAbortedSilent') {
          throw 'TransferCanceled';
        }

        if (chunksQueue) {
          chunksQueue.abort();
        }
        chunksQueue = false;

        this.state = 'aborted';

        throw 'TransferFailed';
      }

      // Finalize the transfer and return short url or smth.
      try {
        if (!this.id) {
          throw 'MissingTransferId';
        }
        transferDetails = await API.finalizeTransfer(this.id);
      } catch (error) {
        this.trackTransferError(error);

        this.state = 'aborted';

        throw 'TransferFailed';
      }

      // NOTE: This logic also exists somewhere else in the API, maybe it should
      // be centralized to just 1 location at some point in time.
      this.expiry_seconds = transferDetails.expiry_in_seconds;
      this.shortURL = transferDetails.shortened_url;
      this.securityHash = transferDetails.security_hash;
    }

    // Set the state to finished
    this.state = 'finished';

    return this;
  }

  async uploadFiles() {
    chunksQueue = new ChunksQueue({ processChunkTask });

    for (const item of this.files) {
      // If item is not a file, aka, is an empty directory, don't try to upload
      if (!item.isFile) continue;

      item.id = await TransferAPI.fetchFileObjectId(this.id, {
        name: item.name,
        size: item.size,
      });

      await uploadFile(this.id, item, chunksQueue);
    }
  }

  trackTransferError(error) {
    TrackJS.addMetadata('transfer_id', this.id);
    TrackJS.track(error);
  }

  get isProcessing() {
    if (this.versioning) {
      return !this.items.length && this.versioning.processing === true;
    }
    return this.state === 'processing';
  }
}
